import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/providers';
import { authService } from '@/services/auth.service';
import { ROUTES } from './routes';
import { LoadingScreen } from '@/components/common/LoadingScreen';

// Routes that are allowed when college status is pending (onboarding routes)
const ALLOWED_ROUTES_WHEN_PENDING = [
  ROUTES.collegeDetailsVerification,
  ROUTES.collegeVerification,
  ROUTES.collegeCompaniesDetails,
];

// Routes that should redirect to dashboard for approved/inreview status
// Note: collegeCompaniesDetails is excluded because it's shown after verification (inreview)
const ROUTES_TO_REDIRECT_WHEN_APPROVED = [
  ROUTES.collegeDetailsVerification,
  ROUTES.collegeVerification,
];

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, collegeProfile } = useAuth();
  const location = useLocation();

  // Show loading screen while checking auth
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Check both React state and localStorage token (for immediate post-login navigation)
  const hasToken = !!authService.getAccessToken();

  // Redirect to login if not authenticated and no token
  if (!isAuthenticated && !hasToken) {
    return <Navigate to={ROUTES.signIn} state={{ from: location }} replace />;
  }

  // Check college status for pending - redirect to verification if trying to access main app routes
  // Get status from both React state and localStorage (for immediate post-login)
  const status = collegeProfile?.status?.toLowerCase() || authService.getStatus()?.toLowerCase() || '';
  const isPending = status === 'pending';
  const isOnAllowedRoute = ALLOWED_ROUTES_WHEN_PENDING.some(route =>
    location.pathname.startsWith(route.split(':')[0])
  );

  // If status is pending and not on allowed route, redirect to college details verification
  if (isPending && !isOnAllowedRoute) {
    return <Navigate to={ROUTES.collegeDetailsVerification} replace />;
  }

  // If status is approved or inreview and on verification route (not companies page), redirect to dashboard
  const isApprovedOrInreview = status === 'approved' || status === 'inreview';
  const isOnRouteToRedirect = ROUTES_TO_REDIRECT_WHEN_APPROVED.some(route =>
    location.pathname.startsWith(route.split(':')[0])
  );
  if (isApprovedOrInreview && isOnRouteToRedirect) {
    return <Navigate to={ROUTES.dashboard} replace />;
  }

  return <>{children}</>;
};

// Public route wrapper - redirects to dashboard if already authenticated
interface PublicRouteProps {
  children: React.ReactNode;
}

export const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, collegeProfile } = useAuth();
  const location = useLocation();

  // Show loading screen while checking auth
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Redirect if already authenticated
  if (isAuthenticated) {
    // Check college status - redirect to verification page if pending
    const status = collegeProfile?.status?.toLowerCase() || authService.getStatus()?.toLowerCase() || '';
    if (status === 'pending') {
      return <Navigate to={ROUTES.collegeDetailsVerification} replace />;
    }
    const from = (location.state as { from?: Location })?.from?.pathname || ROUTES.dashboard;
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
};
